import * as React from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import FormHelperText from "@mui/material/FormHelperText";
import { createTemplate } from "../../apis/templateApi/baseTemplateView";
import {
  ServiceKey,
  serviceTypes,
} from "../../components/Templates/template.types";

const TemplateConfig = () => {
  // Form state
  const [formData, setFormData] = React.useState({
    templateConfigCode: "",
    templateDescription: "",
    azureZone: "",
  });

  // Validation state
  const [formErrors, setFormErrors] = React.useState({
    templateConfigCode: "",
    templateDescription: "",
    azureZone: "",
  });

  // File upload state
  const [files, setFiles] = React.useState<{
    [key in ServiceKey]?: File[];
  }>({});

  // Alert state
  const [alert, setAlert] = React.useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Handle form field changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation errors when user types
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle file upload - allow only one file per service
  const handleFileUpload =
    (serviceKey: ServiceKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        // Only take the first file if multiple are selected
        const newFile = e.target.files[0];
        setFiles((prev) => ({
          ...prev,
          [serviceKey]: [newFile], // Replace any existing file
        }));
      }
    };

  // Remove file from uploaded files
  const handleRemoveFile = (serviceKey: ServiceKey, fileIndex: number) => {
    setFiles((prev) => {
      const updatedFiles = { ...prev };
      if (updatedFiles[serviceKey]) {
        updatedFiles[serviceKey] = updatedFiles[serviceKey]?.filter(
          (_, idx) => idx !== fileIndex
        );
        if (updatedFiles[serviceKey]?.length === 0) {
          delete updatedFiles[serviceKey];
        }
      }
      return updatedFiles;
    });
  };

  // Validate form - check all required fields and all service files
  const validateForm = () => {
    const errors = {
      templateConfigCode: "",
      templateDescription: "",
      azureZone: "",
    };

    let isValid = true;

    if (!formData.templateConfigCode.trim()) {
      errors.templateConfigCode = "Template Configuration Code is required";
      isValid = false;
    }

    if (!formData.templateDescription.trim()) {
      errors.templateDescription = "Template Description is required";
      isValid = false;
    }

    if (!formData.azureZone.trim()) {
      errors.azureZone = "Azure Zone is required";
      isValid = false;
    }

    // Check if all service types have files uploaded
    const serviceKeys = Object.keys(serviceTypes) as ServiceKey[];
    const missingServiceFiles = serviceKeys.filter(
      (key) => !files[key] || files[key]?.length === 0
    );

    if (missingServiceFiles.length > 0) {
      const missingServicesText = missingServiceFiles
        .map((key) => serviceTypes[key].label)
        .join(", ");
      setAlert({
        open: true,
        message: `Missing file uploads for: ${missingServicesText}`,
        severity: "error",
      });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // The validation function now handles displaying the specific error message
      return;
    }

    // Create FormData for submission
    const formDataObject = new FormData();

    // Add form fields
    formDataObject.append("template", formData.templateConfigCode);
    formDataObject.append("templateDescription", formData.templateDescription);
    formDataObject.append("azureZone", formData.azureZone);

    Object.entries(files).forEach(([serviceKey, serviceFiles]) => {
      if (serviceFiles && serviceFiles.length > 0) {
        formDataObject.append(serviceKey, serviceFiles[0]);
        formDataObject.append(`${serviceKey}_filename`, serviceFiles[0].name);
      }
    });

    await createTemplate(formDataObject, () => {
      // Show success alert
      setAlert({
        open: true,
        message: "Template configuration submitted successfully!",
        severity: "success",
      });
    });
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ margin: "0 auto", padding: "20px", overflowY: "auto" }}>
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={3}
        sx={{
          padding: "32px",
          borderRadius: "12px",
          background: "white",
          marginBottom: "24px",
        }}
      >
        <Typography variant="h5" sx={{ mb: 4, fontWeight: 500, color: "#555" }}>
          Template Configuration
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="templateConfigCode"
              label="Template Configuration Code"
              value={formData.templateConfigCode}
              onChange={handleFormChange}
              placeholder="Enter template code (e.g. TEMP001)"
              variant="outlined"
              error={!!formErrors.templateConfigCode}
              helperText={
                formErrors.templateConfigCode ||
                "A unique identifier for this template"
              }
              sx={{ mb: 2 }}
              InputProps={{
                sx: { borderRadius: "8px" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="templateDescription"
              label="Template Description"
              value={formData.templateDescription}
              onChange={handleFormChange}
              placeholder="Enter a detailed description of the template"
              variant="outlined"
              error={!!formErrors.templateDescription}
              helperText={
                formErrors.templateDescription ||
                "Describe the purpose and usage of this template"
              }
              sx={{ mb: 2 }}
              InputProps={{
                sx: { borderRadius: "8px" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="azureZone"
              label="Azure Zone"
              value={formData.azureZone}
              onChange={handleFormChange}
              placeholder="Enter Azure zone (e.g. eastus, westeurope)"
              variant="outlined"
              error={!!formErrors.azureZone}
              helperText={
                formErrors.azureZone ||
                "The Azure deployment region for this template"
              }
              sx={{ mb: 2 }}
              InputProps={{
                sx: { borderRadius: "8px" },
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
            Template Files
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Grid container spacing={3}>
          {(Object.keys(serviceTypes) as ServiceKey[]).map((serviceKey) => {
            const service = serviceTypes[serviceKey];
            const IconComponent = service.icon;
            const hasFiles = files[serviceKey] && files[serviceKey]!.length > 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={serviceKey}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    border: hasFiles
                      ? "1px solid #734bd1"
                      : "1px solid #e0e0e0",
                    transition: "all 0.3s ease",
                    backgroundColor: hasFiles
                      ? "rgba(115, 75, 209, 0.05)"
                      : "white",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <IconComponent sx={{ color: "#734bd1", mr: 1 }} />
                      <Typography sx={{ color: "#734bd1", fontWeight: 500 }}>
                        {service.label}
                      </Typography>
                      {hasFiles && (
                        <Chip
                          size="small"
                          label="1 file"
                          color="success"
                          variant="outlined"
                          sx={{ ml: 1, height: "20px" }}
                        />
                      )}
                    </Box>

                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{
                        bgcolor: "#734bd1",
                        borderRadius: "8px",
                        textTransform: "none",
                        py: 1,
                        "&:hover": {
                          bgcolor: "#6039c8",
                        },
                      }}
                    >
                      {hasFiles ? "Replace File" : "Upload Template"}
                      <input
                        type="file"
                        hidden
                        onChange={handleFileUpload(serviceKey)}
                        accept={service.acceptedFiles}
                      />
                    </Button>

                    <FormHelperText
                      sx={{
                        mt: 1,
                        ml: 1,
                        color: hasFiles ? "success.main" : "text.secondary",
                      }}
                    >
                      {hasFiles
                        ? "File uploaded successfully"
                        : `${service.helperText} (Required)`}
                    </FormHelperText>

                    {hasFiles && (
                      <Box sx={{ mt: 2 }}>
                        <List dense disablePadding>
                          {files[serviceKey]?.map((file, index) => (
                            <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                              <Chip
                                label={file.name}
                                variant="outlined"
                                onDelete={() =>
                                  handleRemoveFile(serviceKey, index)
                                }
                                sx={{
                                  maxWidth: "100%",
                                  "& .MuiChip-label": {
                                    whiteSpace: "normal",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{
              bgcolor: "#734bd1",
              borderRadius: "30px",
              px: 5,
              py: 1.5,
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(115, 75, 209, 0.3)",
              "&:hover": {
                bgcolor: "#6039c8",
                boxShadow: "0 6px 16px rgba(115, 75, 209, 0.4)",
              },
            }}
          >
            Submit Configuration
          </Button>
        </Box>
      </Paper>

      {/* Debug panel to show submitted data - can be removed in production */}
      <div id="formDataDebug" style={{ display: "none" }}></div>
    </Box>
  );
};

export default TemplateConfig;
